import { useState } from 'react';
import { Link } from 'react-router-dom';
import { testimonials, categories } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormState('submitting');
    setFormMessage('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormState('success');
      setFormMessage('提交成功！我们会尽快回复您。');
      form.reset();
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qoebsnlsngrm4lm30g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        setFormMessage('提交成功！我们会尽快回复您。');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormState('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormState('error');
      setFormMessage('网络错误，请稍后再试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '搞笑排行榜',
    url: 'https://ggqcmf.cn/',
    description: '搞笑排行榜汇聚全网最火爆的搞笑视频、段子、表情包排行榜，每日更新最好笑的内容TOP10。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@ggqcmf.cn',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '搞笑排行榜的内容多久更新一次？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的榜单每日更新，每天都有新鲜的搞笑内容排行榜上线。部分热门分类如搞笑视频和搞笑段子更新频率更高，确保您随时都能看到最新的搞笑内容。',
        },
      },
      {
        '@type': 'Question',
        name: '如何给搞笑排行榜投稿？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系表单给我们投稿。请提供搞笑内容的来源链接和简要说明，我们的编辑团队会在审核后决定是否收录到榜单中。',
        },
      },
      {
        '@type': 'Question',
        name: '搞笑排行榜的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的内容来源于全网各大平台的热门搞笑内容，包括但不限于微博、抖音、B站、小红书、知乎等。所有榜单均由编辑团队根据内容的搞笑程度、传播热度和用户反馈综合评定。',
        },
      },
      {
        '@type': 'Question',
        name: '搞笑排行榜有APP吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '目前我们的主要平台是网站，APP正在开发中。您可以通过浏览器访问我们的网站，也可以将网站添加到手机主屏幕，获得接近APP的使用体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何与搞笑排行榜进行商业合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们欢迎各类商业合作，包括广告投放、品牌联动、内容定制等。请通过本页的联系表单选择"商务合作"类型，我们的商务团队会在3个工作日内与您联系。',
        },
      },
    ],
  };

  return (
    <div className="min-h-screen pt-16">
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      {/* Header */}
      <div className="bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">关于我们</h1>
        </div>
      </div>

      {/* Brand Introduction */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
              搞笑排行榜 —— 你的每日快乐源泉
            </h2>
            <div className="space-y-4 text-foreground-600 leading-relaxed">
              <p>
                搞笑排行榜创立于2024年，是国内领先的搞笑内容聚合平台。我们致力于为每一位用户提供最好笑、最有趣、最解压的内容体验。无论是忙碌的工作间隙，还是睡前的放松时刻，搞笑排行榜都是你的快乐加油站。
              </p>
              <p>
                我们的编辑团队每天在全网海量内容中精挑细选，经过多轮筛选和评定，最终形成每个分类的TOP10排行榜。从搞笑视频到神评论，从表情包到奇葩事件，我们覆盖了搞笑内容的方方面面。
              </p>
              <p>
                成立以来，搞笑排行榜已累计服务超过百万用户，日均页面访问量突破50万。我们相信，快乐是生活中最重要的调味剂，而搞笑排行榜就是帮你找到快乐的最佳途径。
              </p>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-accent-500 font-heading">100万+</div>
                <div className="text-xs text-foreground-500 mt-1">累计粉丝</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary-500 font-heading">50万+</div>
                <div className="text-xs text-foreground-500 mt-1">日访问量</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-accent-500 font-heading">1000+</div>
                <div className="text-xs text-foreground-500 mt-1">期榜单</div>
              </div>
            </div>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20cozy%20illustration%20of%20a%20modern%20creative%20office%20space%20with%20playful%20decor%20colorful%20posters%20on%20the%20wall%20people%20laughing%20together%20bright%20natural%20lighting%20warm%20coral%20and%20orange%20accents%20joyful%20atmosphere%20modern%20illustration%20style%20clean%20design&width=800&height=600&seq=about-brand-01&orientation=landscape"
              alt="搞笑排行榜团队"
              title="搞笑排行榜品牌介绍"
              className="w-full h-auto object-cover"
            />
          </div>
        </div>
      </section>

      {/* Categories Overview */}
      <section className="bg-background-100 py-14">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              六大分类，全方位承包你的笑点
            </h2>
            <p className="text-foreground-500 text-sm max-w-lg mx-auto">
              无论你喜欢什么类型的搞笑内容，这里都有你的专属快乐
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="bg-background-50 border border-background-200/70 rounded-xl p-5 hover:border-primary-300 transition-all duration-300 group cursor-pointer"
              >
                <div className="flex items-center gap-4 mb-3">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors text-lg">
                    <i className={cat.icon}></i>
                  </span>
                  <h3 className="font-heading text-lg font-bold text-foreground-900">{cat.name}</h3>
                </div>
                <p className="text-sm text-foreground-500 leading-relaxed">{cat.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            用户口碑
          </h2>
          <p className="text-foreground-500 text-sm max-w-lg mx-auto">
            百万用户的选择和信赖，听听他们怎么说
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {testimonials.map((t, index) => (
            <div
              key={index}
              className="bg-background-50 border border-background-200/70 rounded-xl p-5"
            >
              <div className="flex items-center gap-3 mb-4">
                <img
                  src={t.avatar}
                  alt={t.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                  <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                  <div className="text-xs text-foreground-400">{t.date}</div>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-500 text-sm">关于搞笑排行榜，你可能想了解的一些事</p>
          </div>
          <div className="space-y-4">
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                搞笑排行榜的内容多久更新一次？
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们的榜单每日更新，每天都有新鲜的搞笑内容排行榜上线。部分热门分类如搞笑视频和搞笑段子更新频率更高，确保您随时都能看到最新的搞笑内容。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                如何给搞笑排行榜投稿？
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                您可以通过下方的联系表单给我们投稿。请提供搞笑内容的来源链接和简要说明，我们的编辑团队会在审核后决定是否收录到榜单中。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                搞笑排行榜的内容来源是什么？
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们的内容来源于全网各大平台的热门搞笑内容，包括但不限于微博、抖音、B站、小红书、知乎等。所有榜单均由编辑团队根据内容的搞笑程度、传播热度和用户反馈综合评定。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                搞笑排行榜有APP吗？
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                目前我们的主要平台是网站，APP正在开发中。您可以通过浏览器收藏我们的网站，也可以将网站添加到手机主屏幕，获得接近APP的使用体验。
              </p>
            </div>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
              <h3 className="font-heading text-base font-bold text-foreground-900 mb-2">
                如何与搞笑排行榜进行商业合作？
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们欢迎各类商业合作，包括广告投放、品牌联动、内容定制等。请通过下方的联系表单选择"商务合作"类型，我们的商务团队会在3个工作日内与您联系。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="max-w-3xl mx-auto px-4 md:px-6 py-14">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
            联系我们
          </h2>
          <p className="text-foreground-500 text-sm">
            有任何问题、建议或合作意向，欢迎给我们留言
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-readdy-form
          className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                姓名 <span className="text-accent-500">*</span>
              </label>
              <input
                type="text"
                id="contact-name"
                name="name"
                required
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                placeholder="请输入您的姓名"
              />
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                邮箱 <span className="text-accent-500">*</span>
              </label>
              <input
                type="email"
                id="contact-email"
                name="email"
                required
                className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                placeholder="请输入您的邮箱"
              />
            </div>
          </div>

          <div className="mt-5">
            <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-700 mb-1.5">
              留言类型 <span className="text-accent-500">*</span>
            </label>
            <select
              id="contact-type"
              name="type"
              required
              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
            >
              <option value="">请选择留言类型</option>
              <option value="投稿合作">投稿合作</option>
              <option value="商务合作">商务合作</option>
              <option value="问题反馈">问题反馈</option>
              <option value="意见建议">意见建议</option>
              <option value="其他">其他</option>
            </select>
          </div>

          <div className="mt-5">
            <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
              留言内容 <span className="text-accent-500">*</span>
            </label>
            <textarea
              id="contact-message"
              name="message"
              required
              rows={5}
              maxLength={500}
              className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
              placeholder="请输入您的留言内容（不超过500字）"
            ></textarea>
            <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
          </div>

          {/* Honeypot */}
          <div className="form-honeypot">
            <input
              type="text"
              name="phone_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={formState === 'submitting'}
              className="w-full md:w-auto px-8 py-2.5 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
            >
              {formState === 'submitting' ? '提交中...' : '提交留言'}
            </button>
          </div>

          {formState === 'success' && (
            <div className="mt-4 px-4 py-3 bg-accent-100/80 text-accent-800 rounded-lg text-sm">
              <i className="ri-check-line mr-1"></i>
              {formMessage}
            </div>
          )}
          {formState === 'error' && (
            <div className="mt-4 px-4 py-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <i className="ri-error-warning-line mr-1"></i>
              {formMessage}
            </div>
          )}
        </form>
      </section>
    </div>
  );
}